import { FormControlLabel, TextField } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import ValidTillInput from "~/mui/ValidTillInput";
import { MySwitch } from "~/mui/MySwitch";
import { useTrans } from '~/hooks/useTrans';
import { apiUpdateProjectAdminSalesAttributes } from '~/providers/api';
import { IAdminSalesAttributes, IAdminSalesAttributesChangeData } from '~/providers/admin-interfaces';
import { IUrlParams } from "~/interfaces";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { checkForErrorsState, getDateIsoString } from "~/utils/utils";
import ThreeStateToggle from "~/mui/ThreeStateToggle";
import { userStore } from "~/stores/user";

import "./style.less";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
	actionRequired: boolean;
}

// chyby formulare
type TStateErrors = {[key in keyof Pick<IAdminSalesAttributesChangeData, "actionDescription" | "actionEmails">]: string; };

interface IAdminEditSalesAttributesModal {
	params: IUrlParams;
	salesAttrData: IAdminSalesAttributes;
	onSave?: (salesAttr: IAdminSalesAttributes) => void;
	onClose?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function AdminEditSalesAttributesModal({
	params = null,
	salesAttrData = null,
	onSave = () => {},
	onClose = () => {},
	onChange = () => {},
}: IAdminEditSalesAttributesModal) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
		actionRequired: salesAttrData.actionRequired,
	});
	const { userData } = userStore(userState => ({
		userData: userState.user,
	}));
	const { state: stateFormData, updateState: updateFormData } = myUseState<IAdminSalesAttributesChangeData>({
		actionDescription: salesAttrData.actionDescription || "",
		approvedAdvancedPlan: salesAttrData.approvedAdvancedPlan,
		internalDescription: salesAttrData.internalDescription,
		actionEmails: salesAttrData.actionEmails || "",
		actionValidFrom: salesAttrData.actionValidFrom || "",
		heliosNumber: salesAttrData.heliosNumber || "",
		orderNumber: salesAttrData.orderNumber || "",
		companyNumber: salesAttrData.companyNumber || "",
	});
	const { state: stateError, setState: setStateError } = myUseState<TStateErrors>({
		actionDescription: "",
		actionEmails: "",
	});

	function getWasChangedState(newState?: Partial<IAdminSalesAttributesChangeData>): boolean {
		const newStateData = {
			...stateFormData,
			...newState,
		};

		return newStateData.approvedAdvancedPlan !== salesAttrData.approvedAdvancedPlan
			|| newStateData.actionDescription !== salesAttrData.actionDescription
			|| newStateData.actionEmails !== salesAttrData.actionEmails
			|| newStateData.actionValidFrom !== (salesAttrData.actionValidFrom || "")
			|| newStateData.heliosNumber !== (salesAttrData.heliosNumber || "")
			|| newStateData.orderNumber !== (salesAttrData.orderNumber || "")
			|| newStateData.companyNumber !== (salesAttrData.companyNumber || "");
	}

	function updateAction(actionRequired: boolean) {
		const changeData = {
			actionValidFrom: "",
			actionDescription: "",
			actionEmails: actionRequired
				? userData.email || ""
				: "",
		};
		const wasChanged = getWasChangedState(changeData);

		updateFormData(changeData);
		updateState({
			actionRequired,
			wasChanged,
		});
		setStateError({
			actionEmails: "",
			actionDescription: "",
		});
	}

	function updateFormDataWithChange(stateData?: Partial<IAdminSalesAttributesChangeData>) {
		const wasChanged = getWasChangedState(stateData);

		updateState({
			wasChanged,
		});
		updateFormData({
			...stateData,
			..."actionValidFrom" in stateData && stateData.actionValidFrom
				? {
					actionEmails: salesAttrData.actionEmails || userData.email,
				}
				: {},
		});
		onChange(wasChanged);
	}

	function getAdvancedPlanDesc(): [string, string, string] {
		return [
			trans("projectBusinessPage.attributes.approvedAdvancedPlan.values.false"),
			trans("projectBusinessPage.attributes.approvedAdvancedPlan.values.null"),
			trans("projectBusinessPage.attributes.approvedAdvancedPlan.values.true"),
		];
	}

	async function dialogOnSave() {
		const saveData = {
			...stateFormData,
		};
		const keys: Array<keyof IAdminSalesAttributesChangeData> = ["heliosNumber", "companyNumber", "orderNumber", "actionDescription", "actionValidFrom", "actionEmails"];

		// odstranime bila mista
		saveData.actionEmails = stateFormData.actionValidFrom
			? saveData.actionEmails.replace(/\s/gu, "")
			: "";
		keys.forEach(key => {
			if (saveData[key] as string === "") {
				// @ts-ignore
				saveData[key] = null;
			}
		});

		if (state.actionRequired) {
			const errorData = checkForErrorsState<TStateErrors, TStateErrors>(saveData, {
				actionDescription: { check: "string-gt5", msg: trans("adminEditSalesAttributesModal.error.actionDescription") },
				...stateFormData.actionValidFrom
					? {
						actionEmails: { check: "emails", msg: trans("adminEditSalesAttributesModal.error.actionEmails") },
					}
					: {},
			});

			setStateError({
				actionEmails: "",
				...errorData.errorsState,
			});

			// jsou tam chyby
			if (errorData.errorsCount > 0) {
				return;
			}
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const editData = await apiUpdateProjectAdminSalesAttributes(params.projectId, saveData, lang);

		if (editData.noErrors) {
			onSave(editData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: editData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("adminEditSalesAttributesModal.title")} onClose={onClose} onSave={dialogOnSave} className="adminEditSalesAttributesModal" contentClassName="adminEditSalesAttributesModalContent" dialogLoading={state.dialogLoading}
		disableSave={!state.wasChanged} errorMsg={state.errorMsg} confirmClose={state.wasChanged} minWidth="550px">
		<div>
			<ThreeStateToggle value={stateFormData.approvedAdvancedPlan} onChange={newValue => updateFormDataWithChange({ approvedAdvancedPlan: newValue })} title={trans("projectBusinessPage.attributes.approvedAdvancedPlan")} desc={getAdvancedPlanDesc()} />
		</div>
		<div>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={state.actionRequired} onChange={event => updateAction(event.target.checked)} />}
				label={trans("adminEditSalesAttributesModal.actionRequired")}
			/>
		</div>
		{ state.actionRequired && <div className="adminEditSalesAttributesModal__actionArea">
			<ValidTillInput value={stateFormData.actionValidFrom} label={trans("adminEditSalesAttributesModal.actionValidFrom")} isCzech={isCzech} onChange={date => updateFormDataWithChange({ actionValidFrom: getDateIsoString(date) })} />
			<TextField
				label={trans("projectBusinessPage.attributes.actionDescription")}
				value={stateFormData.actionDescription}
				style={{ width: "100%" }}
				multiline={true}
				onChange={event => updateFormDataWithChange({ actionDescription: event.target.value })}
				color="success"
				autoComplete="off"
				rows={3}
				error={stateError.actionDescription !== ""}
				helperText={stateError.actionDescription}
			/>
			{ stateFormData.actionValidFrom && <TextField
				label={trans("projectBusinessPage.attributes.actionEmails")}
				value={stateFormData.actionEmails}
				style={{ width: "100%" }}
				multiline={true}
				onChange={event => updateFormDataWithChange({ actionEmails: event.target.value })}
				color="success"
				autoComplete="off"
				rows={3}
				error={stateError.actionEmails !== ""}
				helperText={stateError.actionEmails}
			/> }
		</div> }
		<TextField
			label={trans("projectBusinessPage.attributes.heliosNumber")}
			value={stateFormData.heliosNumber}
			onChange={event => updateFormDataWithChange({ heliosNumber: event.target.value })}
			sx={{ width: "100%" }}
		/>
		<TextField
			label={trans("projectBusinessPage.attributes.orderNumber")}
			value={stateFormData.orderNumber}
			onChange={event => updateFormDataWithChange({ orderNumber: event.target.value })}
			sx={{ width: "100%" }}
		/>
		<TextField
			label={trans("projectBusinessPage.attributes.companyNumber")}
			value={stateFormData.companyNumber}
			onChange={event => updateFormDataWithChange({ companyNumber: event.target.value })}
			sx={{ width: "100%" }}
		/>
	</MyDialog>;
}
